import React, { useMemo, useState, useEffect } from 'react';
import ProductCard from '../components/ProductCard';
import { supabase } from '../supabaseClient';

const ProductDetailPage = ({ productId, onAddToCart, onProductClick }) => {
  const [product, setProduct] = useState(null);
  const [mainImage, setMainImage] = useState('');
  const [quantity, setQuantity] = useState(1);

  // carrusel index
  const [currentIndex, setCurrentIndex] = useState(0);

  // productos relacionados
  const [relatedProducts, setRelatedProducts] = useState([]);

  // mapas para poder construir la URL bonita al hacer click en un relacionado
  // categoryMap[category_id] = slug
  // subcatMap[subcategory_id] = slug
  const [categoryMap, setCategoryMap] = useState({});
  const [subcatMap, setSubcatMap] = useState({});

  // =========================
  // Cargar mapas categorías/subcategorías (id -> slug)
  // =========================
  useEffect(() => {
    const fetchTaxonomy = async () => {
      try {
        const { data: cats, error: catsErr } = await supabase
          .from('categories')
          .select('id, slug, is_active')
          .eq('is_active', true);

        if (catsErr) {
          console.error('Error cargando categories:', catsErr.message);
        }

        const { data: subs, error: subsErr } = await supabase
          .from('subcategories')
          .select('id, slug, is_active')
          .eq('is_active', true);

        if (subsErr) {
          console.error('Error cargando subcategories:', subsErr.message);
        }

        const catMapTmp = {};
        (cats || []).forEach((c) => {
          if (c && c.id != null) {
            catMapTmp[c.id] = c.slug;
          }
        });

        const subMapTmp = {};
        (subs || []).forEach((s) => {
          if (s && s.id != null) {
            subMapTmp[s.id] = s.slug;
          }
        });

        setCategoryMap(catMapTmp);
        setSubcatMap(subMapTmp);
      } catch (err) {
        console.error('Error inesperado en fetchTaxonomy:', err);
      }
    };

    fetchTaxonomy();
  }, []);

  // =========================
  // Cargar producto + relacionados
  // =========================
  useEffect(() => {
    const fetchProductAndRelated = async () => {
      // 1. Traer producto principal
      const { data: prod, error: prodErr } = await supabase
        .from('products')
        .select('*')
        .eq('id', productId)
        .single();

      if (prodErr || !prod) {
        console.error('Producto no encontrado:', prodErr);
        setProduct(null);
        setRelatedProducts([]);
        return;
      }

      setProduct(prod);
      setMainImage(prod.image || '');
      setQuantity(1);
      setCurrentIndex(0);

      // 2. Buscar relacionados
      //    Lógica:
      //    - si tiene subcategory_id -> otros del MISMO subcategory_id
      //    - si no tiene subcategory_id -> otros del MISMO category_id
      let relQuery = supabase
        .from('products')
        .select('*')
        .neq('id', prod.id)
        .limit(4);

      if (prod.subcategory_id) {
        relQuery = relQuery.eq('subcategory_id', prod.subcategory_id);
      } else if (prod.category_id) {
        relQuery = relQuery.eq('category_id', prod.category_id);
      }

      const { data: rels, error: relErr } = await relQuery;

      if (relErr) {
        console.error('Error cargando relacionados:', relErr.message);
        setRelatedProducts([]);
      } else {
        setRelatedProducts(rels || []);
      }
    };

    fetchProductAndRelated();
  }, [productId]);

  // =========================
  // Galería: array de imágenes
  // =========================
  const imagesArray = useMemo(() => {
    if (!product) return mainImage ? [mainImage] : [];
    if (Array.isArray(product.images) && product.images.length > 0) {
      // merge image principal + arreglo images sin duplicar
      const merged = [product.image, ...product.images].filter(Boolean);
      const unique = [...new Set(merged)];
      return unique;
    }
    return product.image ? [product.image] : [];
  }, [product, mainImage]);

  // sync mainImage cuando cambie currentIndex
  useEffect(() => {
    if (imagesArray[currentIndex]) {
      setMainImage(imagesArray[currentIndex]);
    }
  }, [currentIndex, imagesArray]);

  // autoplay del carrusel
  useEffect(() => {
    if (imagesArray.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % imagesArray.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [imagesArray.length]);

  // mover carrusel
  const goPrev = () => {
    setCurrentIndex((prev) => (prev - 1 + imagesArray.length) % imagesArray.length);
  };
  const goNext = () => {
    setCurrentIndex((prev) => (prev + 1) % imagesArray.length);
  };

  // =========================
  // Cantidad / carrito
  // =========================
  const handleQuantityChange = (e) => {
    const value = parseInt(e.target.value);
    if (value > 0) setQuantity(value);
  };
  const handleIncrement = () => setQuantity((prev) => prev + 1);
  const handleDecrement = () =>
    setQuantity((prev) => (prev > 1 ? prev - 1 : 1));

  const handleAddToCartClick = () => {
    if (product) {
      onAddToCart({ ...product, quantity });
    }
  };

  // =========================
  // Helper para armar la URL bonita de un producto (relacionado)
  // =========================
  const getBasePathForProduct = (p) => {
    const catSlug = categoryMap[p.category_id];
    const subSlug = subcatMap[p.subcategory_id];
    if (catSlug && subSlug) {
      return `/${catSlug}/${subSlug}`;
    }
    if (catSlug) {
      return `/${catSlug}`;
    }
    return '/tecnologia'; // fallback de seguridad
  };

  // =========================
  // Render
  // =========================
  if (!product) {
    return (
      <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-12 text-center text-gray-600">
        Producto no encontrado.
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-12">
      {/* CARD principal */}
      <div className="bg-white rounded-2xl p-6 sm:p-8 flex flex-col lg:flex-row gap-8">
        {/* ---------- Columna izquierda (galería sticky en desktop) ---------- */}
        <div
          className="w-full lg:w-1/2 flex flex-col items-center lg:items-start
                     lg:sticky lg:top-[200px] lg:self-start"
        >
          {/* Imagen principal con flechas */}
          <div className="relative w-full aspect-square max-h-[480px] bg-white rounded-2xl flex items-center justify-center overflow-hidden mb-3">
            {imagesArray.length > 0 && (
              <img
                src={imagesArray[currentIndex]}
                alt={product.name}
                className="max-h-full max-w-full object-contain select-none"
                loading="eager"
                fetchpriority="high"
                decoding="async"
              />
            )}

            {imagesArray.length > 1 && (
              <button
                type="button"
                onClick={goPrev}
                className="absolute left-2 top-1/2 -translate-y-1/2
                           bg-white/90 hover:bg-white
                           text-gray-800
                           rounded-full w-10 h-10 flex items-center justify-center
                           shadow-md border border-gray-300
                           transition"
                aria-label="Imagen anterior"
              >
                <span className="text-xl leading-none">{'‹'}</span>
              </button>
            )}

            {imagesArray.length > 1 && (
              <button
                type="button"
                onClick={goNext}
                className="absolute right-2 top-1/2 -translate-y-1/2
                           bg-white/90 hover:bg-white
                           text-gray-800
                           rounded-full w-10 h-10 flex items-center justify-center
                           shadow-md border border-gray-300
                           transition"
                aria-label="Imagen siguiente"
              >
                <span className="text-xl leading-none">{'›'}</span>
              </button>
            )}
          </div>

          {/* Indicadores (bolitas) */}
          {imagesArray.length > 1 && (
            <div className="flex items-center justify-center lg:justify-start gap-2 mb-4 w-full">
              {imagesArray.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentIndex(i)}
                  className={
                    'w-3 h-3 rounded-full border border-gray-400 transition ' +
                    (i === currentIndex
                      ? 'bg-brand border-brand shadow-[0_0_6px_rgba(var(--brand-rgb),0.6)]'
                      : 'bg-transparent')
                  }
                  aria-label={`Ir a imagen ${i + 1}`}
                />
              ))}
            </div>
          )}

          {/* Thumbnails */}
          {imagesArray.length > 1 && (
            <div className="flex w-full gap-2 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-neutral-300">
              {imagesArray.map((img, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`shrink-0 rounded-xl border-2 p-1 transition ${
                    index === currentIndex
                      ? 'border-brand bg-white shadow-[0_0_8px_rgba(var(--brand-rgb),0.4)]'
                      : 'border-gray-200 bg-white hover:border-brand/60'
                  }`}
                  aria-label={`Seleccionar imagen ${index + 1}`}
                >
                  <img
                    src={img}
                    alt={`${product.name} thumbnail ${index + 1}`}
                    className="w-20 h-20 object-cover rounded-lg"
                  />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* ---------- Columna derecha ---------- */}
        <div className="w-full lg:w-1/2 flex flex-col justify-between">
          <div>
            <h1 className="text-4xl font-extrabold text-gray-900 mb-3">
              {product.name}
            </h1>

            {/* Pill SKU solo si hay SKU */}
            {product.sku && product.sku.trim() !== '' && (
              <span className="inline-block bg-brand text-white text-xs font-semibold px-2.5 py-0.5 rounded-full mb-4">
                SKU: {product.sku}
              </span>
            )}

            <p className="text-gray-800 text-3xl font-bold mb-6">
              ₡
              {product.price.toLocaleString('es-CR', {
                minimumFractionDigits: 2,
              })}
            </p>

            <div
              className="prose prose-sm list-inside"
              dangerouslySetInnerHTML={{ __html: product.description }}
            />

            <br />

            {/* Cantidad */}
            <div className="flex items-center mb-6">
              <label
                htmlFor="quantity-selector"
                className="text-gray-700 font-medium mr-4"
              >
                Cantidad:
              </label>
              <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden">
                <button
                  onClick={handleDecrement}
                  className="px-4 py-2 bg-gray-100 text-gray-600 hover:bg-gray-200"
                >
                  -
                </button>
                <input
                  type="number"
                  id="quantity-selector"
                  value={quantity}
                  onChange={handleQuantityChange}
                  className="w-16 text-center border-l border-r border-gray-300 focus:outline-none"
                  min="1"
                />
                <button
                  onClick={handleIncrement}
                  className="px-4 py-2 bg-gray-100 text-gray-600 hover:bg-gray-200"
                >
                  +
                </button>
              </div>
            </div>
          </div>

          {/* Botón agregar al carrito */}
          <button
            onClick={handleAddToCartClick}
            className="w-full bg-brand text-white py-3.5 rounded-full hover:bg-brand/90 transition-colors duration-300 shadow-lg text-lg font-semibold"
          >
            Agregar al Carrito
          </button>
        </div>
      </div>

      {/* Productos relacionados */}
      {relatedProducts.length > 0 && (
        <div className="mt-12">
          
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 sm:gap-6">
            {relatedProducts.map((p) => {
              const basePath = getBasePathForProduct(p);
              return (
                <ProductCard
                  key={p.id}
                  product={p}
                  onAddToCart={onAddToCart}
                  onProductClick={() => {
                    onProductClick(p.id, basePath);
                  }}
                />
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetailPage;
